import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import { ChangeRequestReviewType } from "~/common/types/graphql";
import {
  GetBatchReviewDryRunResultDocument,
  type GetBatchReviewDryRunResultQuery,
  type GetBatchReviewDryRunResultQueryVariables,
  SubmitBatchReviewJobDocument,
  type SubmitBatchReviewJobMutation,
  type SubmitBatchReviewJobMutationVariables,
} from "~/features/changeRequests/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

export type BatchReviewAction = "approve" | "reject";

interface SubmitBatchReviewArgs {
  action: BatchReviewAction;
  batchJobId: string;
  batchJobRunId?: string | null;
  changeRequestIds: string[];
  comment?: string;
  dryRun: boolean;
}

/** Submit a batch approve/reject job. Returns the job run id, or null on error. */
export function useSubmitBatchReview() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [mutate] = useMutation<SubmitBatchReviewJobMutation, SubmitBatchReviewJobMutationVariables>(
    SubmitBatchReviewJobDocument,
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async ({
      action,
      batchJobId,
      batchJobRunId,
      changeRequestIds,
      comment,
      dryRun,
    }: SubmitBatchReviewArgs): Promise<string | null> => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return null;
      }
      setSubmitting(true);
      try {
        const res = await mutate({
          variables: {
            input: {
              tenantId,
              type: action === "approve" ? ChangeRequestReviewType.BatchApprove : ChangeRequestReviewType.Reject,
              batchChangeRequestsJobId: batchJobId,
              batchChangeRequestsJobRunId: batchJobRunId ?? undefined,
              changeRequestIds: changeRequestIds.length > 0 ? changeRequestIds : undefined,
              comment: comment?.trim() || undefined,
              dryRun: dryRun || undefined,
            },
          },
        });
        return match(res.data?.cnutSubmitBatchChangeRequestReviewsJob)
          .with({ __typename: "BatchChangeRequestReviewsJobSubmitOutput" }, (o) => o.jobRunId)
          .with({ __typename: "ValidationError" }, (e) => {
            toast.error(e.fields.map((f) => f.message).join("; ") || t("error.unknown"));
            return null;
          })
          .otherwise(() => {
            toast.error(t("error.unknown"));
            return null;
          });
      } catch (e) {
        toast.error(translateError(t, e));
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [mutate, tenantId, t],
  );

  return { submit, submitting };
}

type DryRunResult = GetBatchReviewDryRunResultQuery["cnutGetBatchChangeRequestReviewsJobDryRunResult"];

/** Poll the dry-run result for a job run until it is ready (the backend computes it async). */
export function useBatchReviewDryRun(jobRunId: string | null) {
  const { tenantId } = useTenant();
  const { data, stopPolling } = useQuery<GetBatchReviewDryRunResultQuery, GetBatchReviewDryRunResultQueryVariables>(
    GetBatchReviewDryRunResultDocument,
    {
      skip: !jobRunId || !tenantId,
      variables: { input: { tenantId: tenantId ?? "", jobRunId: jobRunId ?? "" } },
      fetchPolicy: "network-only",
      pollInterval: 3000,
      notifyOnNetworkStatusChange: true,
    },
  );

  const result: DryRunResult | null = data?.cnutGetBatchChangeRequestReviewsJobDryRunResult ?? null;
  const ready = result?.__typename === "DryRunBatchChangeRequestReviewsJobOutput";

  // The result query returns a ValidationError ("not ready") until the async job finishes; stop once ready.
  useEffect(() => {
    if (ready) stopPolling();
  }, [ready, stopPolling]);

  return { result, ready, pending: Boolean(jobRunId) && !ready };
}
